import {
  Modal,
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Grid,
} from "@mui/material"
import { z } from "zod"
import { useAddModel } from "@/hooks/useAddModel"
import ClearIcon from "@mui/icons-material/Clear"
import { useEffect } from "react"

const AttributeNameSchema = z.object({
  value: z.string(),
})

type ReasonFormValue = z.infer<typeof AttributeNameSchema>

interface AddAttributeNameModelProps {
  open?: boolean
  onClose?: () => void
  defaultValues?: Partial<ReasonFormValue>
  getNewRecord?: (response: ReasonFormValue) => void
}

const AddAttributeNameModel: React.FC<AddAttributeNameModelProps> = ({
  open,
  onClose,
  defaultValues = {},
  getNewRecord,
}) => {
  const {
    errors,
    isSubmitting,
    newRecord,
    openDialog,
    selectedRow,
    defaultOnClose,
    register,
    handleSubmit,
    onSubmit,
    setValue,
    handleReset,
  } = useAddModel<ReasonFormValue>(
    "settings",
    AttributeNameSchema,
    "settings",
    onClose,
    (data) => ({
      ...data,
      key: "attribute",
    })
  )

  const isEditing = Boolean(selectedRow)

  useEffect(() => {
    if (defaultValues) {
      Object.entries(defaultValues).forEach(([key, value]) => {
        setValue(key as keyof ReasonFormValue, value as any)
      })
    }
  }, [defaultValues, setValue])

  useEffect(() => {
    if (newRecord && getNewRecord) {
      getNewRecord(newRecord)
    }
  }, [newRecord, getNewRecord])

  return (
    <Modal open={open ? open : openDialog} onClose={onClose}>
      <Box
        sx={{
          maxWidth: 500,
          margin: "100px auto",
          padding: 1,
          bgcolor: "white",
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#f6f7f9",
            padding: { xs: "15px 20px", md: "25px" },
          }}
          className="rmu-modal-header"
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{
              fontWeight: "600",
              fontSize: { xs: "16px", md: "18px" },
            }}
            className="text-black"
          >
            {selectedRow ? "Update Attribute Type" : "Add New Attribute Type"}
          </Typography>
          <IconButton
            aria-label="remove"
            size="small"
            onClick={onClose ? onClose : defaultOnClose}
          >
            <ClearIcon />
          </IconButton>
        </Box>
        <Box
          sx={{ padding: 2 }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <TextField
            fullWidth
            label="Name"
            sx={{ marginBottom: 2 }}
            error={!!errors.value}
            helperText={errors.value ? errors.value.message?.toString() : ""}
            {...register("value")}
          />

          <TextField
            fullWidth
            label="Key"
            type="text"
            value="attribute" // Static string
            sx={{ display: "none", marginBottom: 2 }}
            error={!!errors.key}
            helperText={errors.key ? errors.key.message?.toString() : ""}
            InputProps={{
              readOnly: true, // Makes the field non-editable
            }}
            {...register("key")}
          />

          <Grid item xs={12} mt={1}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                gap: "10px",
              }}
            >
              <Button
                onClick={handleReset}
                variant="text"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "11px 30px",
                }}
              >
                Reset
              </Button>

              <Button
                onClick={onClose ? onClose : defaultOnClose}
                variant="outlined"
                color="error"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "11px 30px",
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "11px 30px",
                  color: "#fff !important",
                }}
              >
                {isEditing ? "Update" : "Create"}
              </Button>
            </Box>
          </Grid>
        </Box>
      </Box>
    </Modal>
  )
}

export default AddAttributeNameModel
